import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import { toast } from "react-toastify";
import EyeShowIcon from "../../assets/img/eye-close.png";
import DownloadIcon from "../../assets/img/download.png";
import FilterIcon from "../../assets/img/filter.svg";
import { downloadSearchResults } from "../../api";
import { ANALYTICS_EVENTS } from "../../constants/analyticsEvents";
import {
  getSelectedPropertyExpediaHotelIds,
  convertUserSelectedDates,
  getAgeString,
} from "../../pages/price-check/utils";
import {
  processFiltersForAPI,
  convertFilterOrderToAPI,
  updateFilterSelectionOrder,
} from "../../utils/filterUtils";
import TagPicker from "../common/tagPicker/TagPicker";
import { clearStoredFilterValues } from "./PriceCheckFilterUtils";
import { Can, P } from "../../rbac";
import { getLogger, trackHeapEvent } from "@hoteltradergit/ht-telemetry-ui-lib";

const PriceCheckFilters = ({
  setIsShowHideColumn,
  isPilotHotel = false,
  filtersData = {},
  onFiltersChange,
  formValues = {},
  sortState = {},
  currentFilters = {},
  currentFilterSelectionOrder = [],
  properties = [],
  columns = [],
  setPriceCheckPage,
  searchBasedPropertyObjects = [],
  tabHandlers = {},
  activeTab = "all",
}) => {
  const [isExportLoading, setIsExportLoading] = useState(false);

  // Use the passed props instead of managing local state
  const selectedFilters = currentFilters;
  const filterSelectionOrder = currentFilterSelectionOrder;

  // Dynamic data from API response
  const cancellationPoliciesData = [
    { label: "All cancellation policies", value: "all" },
    ...(filtersData?.refundableOptions || []).map((option) => ({
      label: option === "YES" ? "Refundable" : "Non-refundable",
      value: option,
    })),
  ];

  const ratePlansData = [
    { label: "All rateplans", value: "all" },
    { label: "Cheapest rate plan", value: "cheapest" }, // Deselects previous options but can coexist with new selections
    ...(filtersData?.ratePlans || []).map((rateplan) => ({
      label: rateplan,
      value: rateplan,
    })),
  ];

  const roomTypesData = [
    { label: "All room types", value: "all" },
    { label: "Cheapest room", value: "cheapest" }, // Deselects previous options but can coexist with new selections
    ...(filtersData?.roomNames || []).map((roomName) => ({
      label: roomName,
      value: roomName,
    })),
  ];

  const mealPlansData = [
    { label: "All meal plans", value: "all" },
    ...(filtersData?.mealPlans || []).map((mealPlan) => ({
      label: mealPlan,
      value: mealPlan,
    })),
  ];

  const promotionsData = [
    { label: "All promotions", value: "all" },
    ...(filtersData?.promotions || []).map((promotion) => ({
      label: promotion,
      value: promotion,
    })),
  ];

  // Helper function to handle mutual exclusivity between "all" and other options
  const handleMutualExclusivity = (filterType, value) => {
    // If value is empty or undefined, default to "all"
    if (!value || value.length === 0) {
      return ["all"];
    }

    // Get the last selected item (latest selection)
    const lastSelected = value[value.length - 1];

    // Handle "cheapest" option - it deselects all previous options but can coexist with new selections
    if (lastSelected === "cheapest") {
      // If "cheapest" was selected, return only "cheapest"
      // This will deselect all previous options in the filter
      return ["cheapest"];
    }

    // If "all" was the latest selection, return only "all"
    if (lastSelected === "all") {
      return ["all"];
    } else {
      // If a specific option was selected (not "cheapest" or "all"),
      // keep "cheapest" if it exists, remove "all", and add the new option
      const hasCheapest = value.includes("cheapest");
      const specificOptions = value.filter(
        (option) => option !== "all" && option !== "cheapest"
      );

      // If we had "cheapest" selected, keep it along with new specific options
      if (hasCheapest) {
        return ["cheapest", ...specificOptions];
      } else {
        // No "cheapest", just return specific options
        return specificOptions;
      }
    }
  };

  // Helper function to check if a filter is in default state (only "all" selected)
  const isDefaultFilterValue = (filterValue) => {
    return (
      Array.isArray(filterValue) &&
      filterValue.length === 1 &&
      filterValue[0] === "all"
    );
  };

  const handleFilterChange = (filterType, value) => {
    // Check if this is an attempt to clear an "All" selection
    const wasAllSelection = isDefaultFilterValue(selectedFilters[filterType]);
    const isClearingToEmpty = !value || value.length === 0;

    // If trying to clear an "All" selection, don't trigger API call
    // This prevents unnecessary API calls when users click the cross on "All" options
    if (wasAllSelection && isClearingToEmpty) {
      return; // Exit early, no API call
    }

    // Apply mutual exclusivity logic
    const processedValue = handleMutualExclusivity(filterType, value);

    // Safeguard: If the filter somehow becomes empty, reset to "All"
    const finalValue = processedValue.length === 0 ? ["all"] : processedValue;

    const newFilters = {
      ...selectedFilters,
      [filterType]: finalValue,
    };

    // Update filter selection order using utility function
    const updatedSelectionOrder = updateFilterSelectionOrder(
      filterSelectionOrder,
      filterType,
      processedValue
    );

    // Call parent callback with both UI filters and API filters
    if (onFiltersChange) {
      const apiFilters = processFiltersForAPI(newFilters);
      const apiFilterSelectionOrder = convertFilterOrderToAPI(
        updatedSelectionOrder
      );
      // Pass UI filters to parent for persistence, API filters for search
      onFiltersChange(
        apiFilters,
        apiFilterSelectionOrder,
        newFilters,
        updatedSelectionOrder
      );
    }
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      ratePlans: ["all"],
      roomTypes: ["all"],
      mealPlans: ["all"],
      promotions: ["all"],
      refundableOptions: ["all"],
    };

    // Clear persisted filter values
    clearStoredFilterValues();

    // Reset pagination to page 1
    if (setPriceCheckPage) {
      setPriceCheckPage((prevState) => ({
        ...prevState,
        number: 0, // Reset to first page (0-based)
      }));
    }

    // Call parent callback with cleared filters
    if (onFiltersChange) {
      const apiFilters = {
        ratePlans: [], // Empty array means "ALL rateplans"
        roomNames: [], // Empty array means "ALL room types"
        promotions: [], // Empty array means "ALL promotions"
        mealPlans: [], // Empty array means "ALL meal plans"
        refundableOptions: [], // Empty array means "BOTH" (refundable and non-refundable)
      };
      // Pass UI filters to parent for persistence, API filters for search
      onFiltersChange(apiFilters, [], clearedFilters, []);
    }
  };

  const handleExport = async () => {
    setIsExportLoading(true);
    try {
      // Check if we have the necessary data for export
      if (!formValues || Object.keys(formValues).length === 0) {
        toast.error("Please perform a search first before downloading data.");
        return;
      }

      // Store the current tab's filter state before creating export payload
      // This ensures the last tab's filters are captured
      if (tabHandlers?.storeCurrentTabFilters) {
        const currentTabKey = activeTab === "all" ? "all" : activeTab;
        tabHandlers.storeCurrentTabFilters(currentTabKey);
      }

      // Create tabs array with data from all tabs
      const tabs = [];

      // Helper function to get stored filter state for a tab
      const getStoredFilterState = (tabKey) => {
        if (tabHandlers?.getFilterStateForTab) {
          const storedState = tabHandlers.getFilterStateForTab(tabKey);
          if (storedState) {
            return {
              filters: storedState.selectedFilters,
              filterStack: storedState.filterSelectionOrder,
            };
          }
        }
        // For the currently active tab, use current filters
        // For other tabs, use default filters
        if (
          (tabKey === "all" && activeTab === "all") ||
          (tabKey !== "all" && activeTab === tabKey)
        ) {
          return {
            filters: currentFilters,
            filterStack: filterSelectionOrder,
          };
        } else {
          // Return default filters for inactive tabs
          return {
            filters: {
              ratePlans: ["all"],
              roomTypes: ["all"],
              mealPlans: ["all"],
              promotions: ["all"],
              refundableOptions: ["all"],
            },
            filterStack: [],
          };
        }
      };

      // Add "All properties" tab
      const allPropertiesHotelIds = getSelectedPropertyExpediaHotelIds(
        properties,
        formValues?.selectedCity || [],
        formValues?.selectedProperties || []
      );

      // Get stored filter state for "All properties" tab
      const allTabState = getStoredFilterState("all");

      tabs.push({
        hotelIds: allPropertiesHotelIds,
        filters: processFiltersForAPI(allTabState.filters),
        filterStack: convertFilterOrderToAPI(allTabState.filterStack),
        sortBy: sortState?.sortBy || "departureDate",
        sortDirection: sortState?.sortDirection || "asc",
      });

      // Add individual property tabs
      if (searchBasedPropertyObjects && searchBasedPropertyObjects.length > 0) {
        searchBasedPropertyObjects.forEach((propertyObj) => {
          // Get hotel ID for this property
          const property = properties.find(
            (p) => p.propertyId === propertyObj?.propertyId
          );
          const hotelId = property?.expediaHotelId;

          if (hotelId) {
            // Get stored filter state for this specific property tab
            // Use propertyId as the key for storing/retrieving filter state
            const propertyId = propertyObj?.propertyId || property?.propertyId;
            const propertyTabState = getStoredFilterState(propertyId);

            tabs.push({
              hotelIds: [hotelId],
              filters: processFiltersForAPI(propertyTabState.filters),
              filterStack: convertFilterOrderToAPI(
                propertyTabState.filterStack
              ),
              sortBy: sortState?.sortBy || "departureDate",
              sortDirection: sortState?.sortDirection || "asc",
            });
          }
        });
      }

      const downloadParams = {
        dates: convertUserSelectedDates(formValues?.dateRanges || []),
        guestAges: getAgeString(formValues?.adults, formValues?.childAges),
        bookDate: formValues?.bookDate,
        columns: columns.map((column) => column.dataKey), // Extract dataKey from each column
        tabs: tabs,
      };

      // calling api
      trackHeapEvent(ANALYTICS_EVENTS.PRICECHECK_EXPORT, {
        activeTab,
        selectedProperties: formValues?.selectedProperties,
        selectedCity: formValues?.selectedCity,
        stayDates: formValues?.dateRanges,
        bookingCreationDate: formValues?.bookDate,
        adults: formValues?.adults,
        children: formValues?.childAges?.length || 0,
      });
      const response = await downloadSearchResults(downloadParams);
      let filename = response.headers["content-disposition"];

      if (!filename) {
        toast.success("Exported file will be mailed!");
        setIsExportLoading(false);
        return;
      }

      filename = filename?.split("filename=")[1]?.replace(/"/g, "");
      const url = window.URL.createObjectURL(
        new Blob([response.body], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Download completed successfully!");
    } catch (error) {
      getLogger().error("PriceCheckFilters", {
        type: "Error",
        page_url: window.location.href,
        error_desc: "Error exporting data",
        error_stack: error?.stack ?? null,
      });
      toast.error(
        `Download failed: ${error.message || error.error || "Bad request"}`
      );
    } finally {
      setIsExportLoading(false);
    }
  };

  return (
    <div className="filters">
      <div className="filter-section">
        <div className="filter-header">
          <img src={FilterIcon} alt="filter icon" className="filter-icon" />
          <span className="filter-label">Filter</span>
        </div>

        <div className="filter-dropdowns">
          <TagPicker
            data={cancellationPoliciesData}
            value={selectedFilters.refundableOptions}
            onChange={(value) => handleFilterChange("refundableOptions", value)}
            placeholder="All cancellation policies"
            className="filter-dropdown"
            searchable={false}
            size="md"
            cleanable={false}
          />

          <TagPicker
            data={ratePlansData}
            value={selectedFilters.ratePlans}
            onChange={(value) => handleFilterChange("ratePlans", value)}
            placeholder="All rateplans"
            className="filter-dropdown"
            searchable={true}
            size="md"
            cleanable={false}
          />

          <TagPicker
            data={promotionsData}
            value={selectedFilters.promotions}
            onChange={(value) => handleFilterChange("promotions", value)}
            placeholder="All promotions"
            className="filter-dropdown"
            searchable={true}
            size="md"
            cleanable={false}
          />

          <TagPicker
            data={roomTypesData}
            value={selectedFilters.roomTypes}
            onChange={(value) => handleFilterChange("roomTypes", value)}
            placeholder="All room types"
            className="filter-dropdown"
            searchable={true}
            size="md"
            cleanable={false}
          />

          <TagPicker
            data={mealPlansData}
            value={selectedFilters.mealPlans}
            onChange={(value) => handleFilterChange("mealPlans", value)}
            placeholder="All meal plans"
            className="filter-dropdown"
            searchable={true}
            size="md"
            cleanable={false}
          />
        </div>

        <Button
          appearance="link"
          size="sm"
          onClick={clearAllFilters}
          className="clear-all-btn"
        >
          Clear all
        </Button>
      </div>

      <div className="button-section">
        <Button
          appearance="ghost"
          size="lg"
          onClick={() => setIsShowHideColumn(true)}
        >
          <img src={EyeShowIcon} alt="show hide icon" className="show-icon" />{" "}
          Columns
        </Button>
        <Can permission={P.PriceCheck.Export}>
          <Button
            type="submit"
            appearance="primary"
            size="lg"
            loading={isExportLoading}
            onClick={handleExport}
          >
            <img
              src={DownloadIcon}
              alt="download icon"
              className="download-icon"
            />{" "}
            Download
          </Button>
        </Can>
      </div>
    </div>
  );
};

PriceCheckFilters.propTypes = {
  setIsShowHideColumn: PropTypes.func.isRequired,
  isPilotHotel: PropTypes.bool,
  filtersData: PropTypes.shape({
    ratePlans: PropTypes.array,
    roomNames: PropTypes.array,
    mealPlans: PropTypes.array,
    promotions: PropTypes.array,
    refundableOptions: PropTypes.array,
  }),
  onFiltersChange: PropTypes.func,
  formValues: PropTypes.object,
  sortState: PropTypes.shape({
    sortBy: PropTypes.string,
    sortDirection: PropTypes.string,
  }),
  currentFilters: PropTypes.object,
  currentFilterSelectionOrder: PropTypes.array,
  properties: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      header: PropTypes.string,
      label: PropTypes.string,
      width: PropTypes.number,
    })
  ),
  setPriceCheckPage: PropTypes.func,
  searchBasedPropertyObjects: PropTypes.arrayOf(
    PropTypes.shape({
      propertyId: PropTypes.string.isRequired,
      propertyName: PropTypes.string.isRequired,
    })
  ),
  tabHandlers: PropTypes.shape({
    getFilterStateForTab: PropTypes.func,
    storeCurrentTabFilters: PropTypes.func,
  }),
  activeTab: PropTypes.string,
};

export default PriceCheckFilters;
